import { computed, ref, watch } from 'vue'
import type { QueryClient } from '@tanstack/vue-query'
import { buildApiUrl } from '@/api/http'
import { useAuthStore } from '@/stores/auth'
import { shouldShowPortalMessages } from './portalAccess'
import { portalOfflineState } from './portalOffline'
import { PortalChatCache, type PortalChatUpdate } from './portalChatCache'

const connected = ref(false)
export const portalChatRealtime = { connected: computed(() => connected.value) }
let cache: PortalChatCache | undefined
export const portalChatCache = (account: string): PortalChatCache | undefined => cache?.account === account ? cache : undefined

export function installPortalChatRealtime(client: QueryClient, auth: ReturnType<typeof useAuthStore>): () => void {
  let socket: WebSocket | undefined
  let reconnect: ReturnType<typeof setTimeout> | undefined
  let heartbeat: ReturnType<typeof setInterval> | undefined
  let deadline: ReturnType<typeof setTimeout> | undefined
  let epoch = 0
  let attempt = 0
  let suspended = false
  let lastCatchUp = 0
  const eligible = () => !suspended && !portalOfflineState.readonly.value && shouldShowPortalMessages(auth.session)
  function catchUp(): void {
    if (Date.now() - lastCatchUp < 500) return
    lastCatchUp = Date.now()
    for (const key of ['chats', 'chat-messages', 'chat-unread', 'homework']) void client.invalidateQueries({ queryKey: ['portal', key] })
  }
  function stop(): void {
    ++epoch
    connected.value = false
    clearTimeout(reconnect); clearTimeout(deadline); clearInterval(heartbeat)
    const previous = socket
    socket = undefined
    previous?.close(1000, 'Session changed')
  }
  function connect(): void {
    if (!eligible() || socket || !auth.session) return
    const generation = epoch
    const account = auth.session.accountId
    if (cache?.account !== account) cache = new PortalChatCache(client, account)
    const url = new URL(buildApiUrl('/portal/chats/realtime'), window.location.origin)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(url)
    socket = ws
    const current = () => socket === ws && epoch === generation && auth.session?.accountId === account
    const expire = () => { if (current()) ws.close(4000, 'Heartbeat timeout') }
    deadline = setTimeout(expire, 10_000)
    const ping = () => {
      if (!current() || ws.readyState !== WebSocket.OPEN) return
      ws.send(JSON.stringify({ type: 'ping' }))
      clearTimeout(deadline)
      deadline = setTimeout(expire, 10_000)
    }
    ws.onmessage = event => {
      if (!current()) return
      let data: { type?: string; accountId?: string } & Partial<Omit<PortalChatUpdate, 'type'>>
      try { data = JSON.parse(event.data) } catch { return }
      if (data.type === 'ready') {
        if (data.accountId !== account) { ws.close(1000, 'Account mismatch'); return }
        clearTimeout(deadline)
        connected.value = true
        attempt = 0
        clearInterval(heartbeat)
        heartbeat = setInterval(ping, 20_000)
        catchUp()
      } else if (data.type === 'pong') clearTimeout(deadline)
      else if (data.type === 'chat.updated' && connected.value && typeof data.conversationId === 'string') {
        cache?.apply(data as PortalChatUpdate)
        void client.invalidateQueries({ queryKey: ['portal', 'homework'] })
      }
    }
    ws.onclose = event => {
      if (!current()) return
      socket = undefined
      connected.value = false
      clearTimeout(deadline); clearInterval(heartbeat)
      if (event.code === 1008) {
        // Revalidate revoked/expired credentials instead of reconnecting blindly.
        const requireLogin = () => {
          if (!auth.session) window.dispatchEvent(new CustomEvent('avoschool:auth-required'))
        }
        void auth.initialize(true).then(requireLogin).catch(requireLogin)
      }
      if (eligible()) reconnect = setTimeout(connect, Math.min(15_000, 500 * 2 ** attempt++) + Math.random() * 250)
    }
  }
  const unwatch = watch([() => auth.session, portalOfflineState.readonly], () => {
    stop()
    attempt = 0
    lastCatchUp = 0
    cache = auth.session && eligible() ? new PortalChatCache(client, auth.session.accountId) : undefined
    connect()
  }, { immediate: true, flush: 'sync' })
  const foreground = () => {
    if (document.visibilityState !== 'visible' || !eligible()) return
    if (!socket) connect()
    // A suspended phone can keep a dead socket marked OPEN. Reconnect to verify
    // the session and catch up before trusting another stream of events.
    else { stop(); connect() }
  }
  const hide = () => { suspended = true; stop() }
  const show = () => { suspended = false; connect() }
  document.addEventListener('visibilitychange', foreground)
  window.addEventListener('pagehide', hide)
  window.addEventListener('pageshow', show)
  return () => {
    unwatch(); stop(); cache = undefined
    document.removeEventListener('visibilitychange', foreground)
    window.removeEventListener('pagehide', hide)
    window.removeEventListener('pageshow', show)
  }
}
